import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { TOAST_MS, TOAST_UNDO_MS, ToastCtx, type ToastApi } from '../../hooks/useToast';

interface ToastState {
  message: string;
  undo: (() => void) | null;
}

/**
 * Toast host. Renders `div.wtr-toast.is-on` (styled by dineops-mob.css) with
 * the undo button exactly like the mockup. Portaled to <body> so an ancestor
 * transform can never re-anchor the fixed position.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ToastState | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  const dismiss = useCallback(() => {
    clearTimer();
    setState(null);
  }, []);

  const show = useCallback((message: string, undo: (() => void) | null, ms: number) => {
    clearTimer();
    setState({ message, undo });
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setState(null);
    }, ms);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      toast: (message, ms) => show(message, null, ms ?? TOAST_MS),
      toastUndo: (message, undo) => show(message, undo, TOAST_UNDO_MS),
      dismiss,
    }),
    [show, dismiss],
  );

  useEffect(() => clearTimer, []);

  const runUndo = () => {
    const fn = state?.undo;
    dismiss();
    fn?.();
  };

  const node = (
    <div className={`wtr-toast${state ? ' is-on' : ''}`} role="status" aria-live="polite">
      {state ? <span>{state.message}</span> : null}
      {state?.undo ? (
        <button type="button" className="wtr-toast__undo" onClick={runUndo}>
          Undo
        </button>
      ) : null}
    </div>
  );

  return (
    <ToastCtx.Provider value={api}>
      {children}
      {typeof document === 'undefined' ? node : createPortal(node, document.body)}
    </ToastCtx.Provider>
  );
}
